import React, {ReactElement, useEffect, useCallback, useState} from 'react';
import {Outlet, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {POPUP_TYPES} from '@frontend/ui-kit/Components/Popup';
import Header from '../Header';
import Loader from '../Loader';
import Sidebar from '../Sidebar';
import Toast from '../Toast';
import TermsAndConditionsPopup from '../TermsAndConditionsPopup';
import withPopup from '../../../HOC/withPopup';
import {useGetConsentStatusQuery, useAcceptConsentMutation, useExchangeRefreshTokenMutation, useLazyGetUserQuery} from '../../../api';
import analyticsService, {ANALYTICS_EVENTS} from '../../../analyticsService';
import {logOut} from '../../../slices/globalActions';
import {getServerErrorStatus, setServerErrorStatus} from '../../../slices/shared';
import {getIsAuthorized, getRefreshToken, getUser, setAccessToken, setUser} from '../../../slices/authorization';
import {setCookie} from '../../../helpers/cookies';
import {DASHBOARD_URL, CONSENT_TYPES, ROUTES} from '../../../constants';
import {AppDispatch} from '../../../store';
import {ConsentDocument} from '../../../models/user';
import {CookieNames} from '../../../generalTypes';
import {Props} from './types';
import styles from './index.module.scss';

const POPUP_ID = 'termsAndConditionsPopup';

const BaseInnerLayout = ({openPopup, closePopup}: Props): ReactElement => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [isReady, setIsReady] = useState(false);
    const serverErrorStatus = useSelector(getServerErrorStatus);
    const isAuthorized = useSelector(getIsAuthorized);
    const refreshToken = useSelector(getRefreshToken);
    const user = useSelector(getUser);
    const {data: consentStatus} = useGetConsentStatusQuery({type: CONSENT_TYPES.termsAndConditions}, {skip: !user});
    const [acceptConsent] = useAcceptConsentMutation();
    const [exchangeRefreshToken] = useExchangeRefreshTokenMutation();
    const [getUserInfo] = useLazyGetUserQuery();

    useEffect(() => {
        (async () => {
            if (!isAuthorized) {
                dispatch(logOut());
                return;
            }

            const {data: exchangeData, error: exchangeError} = await exchangeRefreshToken({sso_token: refreshToken as string});

            if (exchangeError || !exchangeData) {
                dispatch(logOut());
                return;
            }

            dispatch(setAccessToken(exchangeData.token));
            setCookie(CookieNames.accessToken, exchangeData.token);

            const {data: userData} = await getUserInfo();
            dispatch(setUser(userData ?? null));

            if (userData) {
                const {user_id: id, is_internal: isInternal, is_admin: isAdmin} = userData;
                analyticsService.identify(id, {is_internal: isInternal, is_admin: isAdmin});
                analyticsService.track(ANALYTICS_EVENTS.common.accessGranted);
            }

            setIsReady(true);
        })();
    }, []);

    useEffect(() => {
        if (serverErrorStatus) {
            dispatch(setServerErrorStatus(null));
            navigate(`${ROUTES.serverError}/${serverErrorStatus}`);
        }
    }, [serverErrorStatus, dispatch, navigate]);

    const onOpenConsentPopup = useCallback((document: ConsentDocument) => {
        const {id: consentDocumentId} = document;
        const onAccept = async () => {
            const {error} = await acceptConsent({consentDocumentId});

            if (!error) {
                analyticsService.track(ANALYTICS_EVENTS.common.termsOfUseAccepted);
                closePopup();
            }
        };
        const onClose = () => {
            closePopup();
            window.location.replace(DASHBOARD_URL);
        };

        openPopup({type: POPUP_TYPES.fullscreen, children: <TermsAndConditionsPopup document={document} onAccept={onAccept} onClose={onClose}/>});
    }, [acceptConsent]);

    useEffect(() => {
        const {hasCurrentAcceptance, consentDocument} = consentStatus || {};

        if (!hasCurrentAcceptance && consentDocument) {
            onOpenConsentPopup(consentDocument);
        }
    }, [consentStatus, onOpenConsentPopup]);

    return (
        <React.Fragment>
            <Loader/>
            <Toast/>

            <div className={styles['inner-layout']}>
                <Header/>

                <div className={styles['main-wrapper']}>
                    <Sidebar/>

                    <div className={styles['main-wrapper__layout']}>
                        {isReady && <Outlet/>}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export {BaseInnerLayout as TestableBaseInnerLayout};
export default withPopup(POPUP_ID)(BaseInnerLayout);
